import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    Box,
    Button,
    Container,
    Typography,
    Grid,
    Link,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Fade,
    Grow,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ReplayIcon from '@mui/icons-material/Replay';
import SpeedIcon from '@mui/icons-material/Speed';
import TimerIcon from '@mui/icons-material/Timer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LockIcon from '@mui/icons-material/Lock';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import { alpha, keyframes } from '@mui/system';

const cyan = '#00BFFF';
const yellow = '#FFD54F';
const darkBg = '#060B14';
const cardBg = '#0E1626';
const cardBorder = 'rgba(0, 191, 255, 0.15)';

const guideDotMove = keyframes`
    0% { left: 8%; }
    100% { left: 88%; }
`;

const glowPulse = keyframes`
    0%, 100% { opacity: 0.4; box-shadow: 0 0 8px 2px ${alpha(cyan, 0.6)}; }
    50% { opacity: 1; box-shadow: 0 0 20px 6px ${alpha(cyan, 0.9)}; }
`;

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
`;

const ghostArrowFade = keyframes`
    0% { opacity: 0; transform: translateX(0); }
    30% { opacity: 0.25; }
    100% { opacity: 0; transform: translateX(-30px); }
`;

const heroLineReveal = keyframes`
    from { opacity: 0; transform: translateX(-10px); }
    to { opacity: 1; transform: translateX(0); }
`;

const instagramPosts = [
    {
        num: '01',
        hook: 'Why do I keep reading the same line over and over?',
        description: 'If your eyes keep jumping backward, the problem may be rhythm, focus, or tracking - not intelligence.',
        blogSlug: 'why-do-i-read-the-same-line-over-and-over',
        buttonText: 'Read the Rereading Guide',
        icon: <ReplayIcon />,
    },
    {
        num: '02',
        hook: "You're not a slow reader. You're using a slow system.",
        description: 'Slow reading usually comes from fixable habits like rereading, subvocalization, weak pacing, and poor focus.',
        blogSlug: 'why-do-i-read-so-slow',
        buttonText: "Find Out What's Slowing You Down",
        icon: <SpeedIcon />,
    },
    {
        num: '03',
        hook: 'Reading a book a week is not magic. It\'s math.',
        description: 'You do not need unlimited free time. You need a repeatable reading plan that fits real life.',
        blogSlug: 'how-to-read-a-book-a-week',
        buttonText: 'See the Book-a-Week Plan',
        icon: <MenuBookIcon />,
    },
    {
        num: '04',
        hook: '100 pages in 60 minutes?',
        description: 'Learn when 100 pages per hour is realistic, what pace it requires, and how to train toward it.',
        blogSlug: 'how-to-read-100-pages-in-an-hour',
        buttonText: 'Read the 100-Page Guide',
        icon: <TimerIcon />,
    },
    {
        num: '05',
        hook: 'Faster reading is useless if you remember nothing.',
        description: 'Speed only matters if comprehension comes with it. Learn how to move faster without losing the meaning.',
        blogSlug: 'read-faster-without-losing-comprehension',
        buttonText: 'Read Faster the Right Way',
        icon: <TrendingUpIcon />,
    },
    {
        num: '06',
        hook: 'You forgot it because you never trained retention.',
        description: 'Reading more is not enough. Build a system for remembering, reviewing, and actually using what you read.',
        blogSlug: 'how-to-remember-everything-you-read',
        buttonText: 'Train Your Retention',
        icon: <LockIcon />,
    },
    {
        num: '07',
        hook: '15 minutes a day can change how you read.',
        description: 'A short daily routine can train eye movement, pacing, focus, comprehension, and recall.',
        blogSlug: '15-minute-speed-reading-routine',
        buttonText: 'Steal the 15-Minute Routine',
        icon: <TimerIcon />,
    },
    {
        num: '08',
        hook: 'The tiny dot that changes how you read.',
        description: 'Your eyes naturally jump, pause, and drift. A moving guide can help train smoother forward rhythm.',
        blogSlug: 'guide-dot-speed-reading',
        buttonText: 'See How the Guide Dot Works',
        icon: <RadioButtonCheckedIcon />,
    },
    {
        num: '09',
        hook: 'Your inner narrator isn\'t always helping.',
        description: 'Subvocalization is normal, but reading every word in your head can cap your speed.',
        blogSlug: 'stop-subvocalizing-read-faster',
        buttonText: 'Quiet the Inner Narrator',
        icon: <RecordVoiceOverIcon />,
    },
    {
        num: '10',
        hook: 'ADHD reading tip: stop trying to force stillness.',
        description: 'Some readers need motion, structure, and feedback to stay engaged with the page.',
        blogSlug: 'speed-reading-adhd',
        buttonText: 'Read the ADHD-Friendly Guide',
        icon: <AutoAwesomeMotionIcon />,
    },
];

const faqs = [
    {
        q: 'Is ReaderFlex just speed reading?',
        a: 'No. ReaderFlex is built around speed, comprehension, focus, and retention. Reading faster only matters if you still understand what you read.',
    },
    {
        q: 'Will this make me read 100 pages an hour?',
        a: 'Not every book, not every reader, and not instantly. The goal is to train better pacing and reduce the habits that slow you down.',
    },
    {
        q: 'What if I read slowly because I have trouble focusing?',
        a: 'ReaderFlex is designed to give your attention a moving structure to follow. Many readers find that guided rhythm makes reading feel less scattered.',
    },
    {
        q: 'Do I have to stop the voice in my head?',
        a: 'No. The goal is not to destroy subvocalization. The goal is to reduce the need to pronounce every single word internally.',
    },
    {
        q: 'Where should I start?',
        a: 'If you keep rereading, start with the rereading guide. If you forget what you read, start with retention. If you want to experience the tool directly, start a ReaderFlex session.',
    },
];

const methodCards = [
    {
        icon: <MenuBookIcon sx={{ fontSize: 40, color: cyan }} />,
        title: 'Stop fighting the page',
        copy: 'Most readers slow down because their eyes keep jumping backward, losing place, or waiting for every word to be spoken internally.',
    },
    {
        icon: <VisibilityIcon sx={{ fontSize: 40, color: cyan }} />,
        title: 'Give your attention a path',
        copy: 'The Guide Dot creates motion and rhythm, giving your focus something to follow instead of forcing stillness.',
    },
    {
        icon: <PsychologyIcon sx={{ fontSize: 40, color: cyan }} />,
        title: 'Train speed with comprehension',
        copy: 'ReaderFlex is not about reckless skimming. It is about controlled speed, better pacing, and remembering more of what matters.',
    },
];

const featureBullets = [
    'Guide Dot pacing to reduce drifting and rereading',
    'Speed controls so you can train gradually',
    'Chunking-friendly reading flow',
    'Comprehension-focused practice',
    'Built for people who want to read more without feeling buried',
];

function GuideDotHero() {
    const lineData = [
        { w: 88, delay: 0 },
        { w: 72, delay: 0.1 },
        { w: 94, delay: 0.2 },
        { w: 65, delay: 0.3 },
        { w: 82, delay: 0.4 },
        { w: 90, delay: 0.5 },
        { w: 58, delay: 0.6 },
        { w: 76, delay: 0.7 },
        { w: 85, delay: 0.8 },
        { w: 70, delay: 0.9 },
    ];
    return (
        <Box
            sx={{
                position: 'absolute',
                right: { xs: '5%', md: '8%' },
                top: { xs: '55%', md: '50%' },
                transform: 'translateY(-50%)',
                width: { xs: 280, sm: 360, md: 460 },
                height: { xs: 220, sm: 280, md: 360 },
                display: { xs: 'none', sm: 'block' },
                zIndex: 1,
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    borderRadius: 4,
                    background: 'linear-gradient(145deg, #0A1120 0%, #0E1A2E 100%)',
                    border: `1px solid ${alpha(cyan, 0.2)}`,
                    boxShadow: `0 0 60px ${alpha(cyan, 0.08)}, inset 0 0 40px ${alpha('#000', 0.5)}`,
                    p: { xs: 2.5, md: 3.5 },
                    overflow: 'hidden',
                }}
            >
                {/* Faint text lines */}
                {lineData.map((line, i) => (
                    <Box
                        key={i}
                        sx={{
                            height: 3,
                            mb: { xs: 2.5, md: 3 },
                            borderRadius: 2,
                            background: `linear-gradient(90deg, ${alpha('#fff', 0.03)} 0%, ${alpha('#fff', 0.07)} 50%, ${alpha('#fff', 0.01)} 100%)`,
                            width: `${line.w}%`,
                            animation: `${heroLineReveal} 0.6s ease ${line.delay}s both`,
                        }}
                    />
                ))}

                {/* Forward path line - glowing cyan track */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '20%',
                        left: 0,
                        right: 0,
                        height: 1,
                        background: `linear-gradient(90deg, transparent, ${alpha(cyan, 0.2)}, transparent)`,
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: '45%',
                        left: 0,
                        right: 0,
                        height: 1,
                        background: `linear-gradient(90deg, transparent, ${alpha(cyan, 0.15)}, transparent)`,
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: '70%',
                        left: 0,
                        right: 0,
                        height: 1,
                        background: `linear-gradient(90deg, transparent, ${alpha(cyan, 0.1)}, transparent)`,
                    }}
                />

                {/* Glowing cyan guide dot - main moving dot */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '20%',
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: cyan,
                        animation: `${guideDotMove} 2.5s ease-in-out infinite alternate, ${glowPulse} 1.5s ease-in-out infinite`,
                    }}
                />

                {/* Second guide dot on a different line */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '45%',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: alpha(cyan, 0.7),
                        animation: `${guideDotMove} 3s ease-in-out infinite alternate 0.5s, ${glowPulse} 2s ease-in-out infinite 0.3s`,
                    }}
                />

                {/* Backward ghost arrows - fading behind the forward motion */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '20%',
                        left: '15%',
                        color: alpha('#fff', 0.15),
                        fontSize: '1.2rem',
                        animation: `${ghostArrowFade} 2.5s ease-in-out infinite`,
                        pointerEvents: 'none',
                    }}
                >
                    {'\u2190'}
                </Box>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '20%',
                        left: '35%',
                        color: alpha('#fff', 0.1),
                        fontSize: '1rem',
                        animation: `${ghostArrowFade} 2.5s ease-in-out infinite 0.5s`,
                        pointerEvents: 'none',
                    }}
                >
                    {'\u2190'}
                </Box>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '45%',
                        left: '25%',
                        color: alpha('#fff', 0.08),
                        fontSize: '0.9rem',
                        animation: `${ghostArrowFade} 3s ease-in-out infinite 0.8s`,
                        pointerEvents: 'none',
                    }}
                >
                    {'\u2190'}
                </Box>

                {/* Subtle corner glow */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -20,
                        right: -20,
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${alpha(cyan, 0.08)} 0%, transparent 70%)`,
                        pointerEvents: 'none',
                    }}
                />
            </Box>
        </Box>
    );
}

function InstagramCard({ post, index }) {
    return (
        <Box
            sx={{
                backgroundColor: cardBg,
                border: `1px solid ${cardBorder}`,
                borderRadius: 3,
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                transition: 'all 0.3s ease',
                animation: `${fadeUp} 0.5s ease ${index * 0.08}s both`,
                '&:hover': {
                    borderColor: alpha(cyan, 0.4),
                    boxShadow: `0 8px 32px ${alpha(cyan, 0.12)}`,
                    transform: 'translateY(-4px)',
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        color: alpha(cyan, 0.5),
                        fontSize: '0.7rem',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                    }}
                >
                    Instagram Post {post.num}
                </Typography>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(cyan, 0.08),
                        border: `1px solid ${alpha(cyan, 0.15)}`,
                        color: cyan,
                        '& svg': { fontSize: '1.1rem' },
                    }}
                >
                    {post.icon}
                </Box>
            </Box>
            <Typography
                variant="h6"
                sx={{
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: { xs: '1.05rem', sm: '1.15rem' },
                    lineHeight: 1.35,
                    mb: 1.5,
                    minHeight: { xs: 'auto', sm: '3.2em' },
                }}
            >
                {post.hook}
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    color: alpha('#fff', 0.6),
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    mb: 2.5,
                    flexGrow: 1,
                }}
            >
                {post.description}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                    component={RouterLink}
                    to={`/blog/${post.blogSlug}`}
                    variant="contained"
                    fullWidth
                    sx={{
                        backgroundColor: alpha(cyan, 0.15),
                        color: cyan,
                        border: `1px solid ${alpha(cyan, 0.3)}`,
                        fontWeight: 600,
                        textTransform: 'none',
                        py: 1,
                        '&:hover': {
                            backgroundColor: alpha(cyan, 0.25),
                            borderColor: cyan,
                        },
                    }}
                >
                    {post.buttonText}
                </Button>
                <Button
                    component={RouterLink}
                    to="/register"
                    fullWidth
                    sx={{
                        color: alpha('#fff', 0.55),
                        fontSize: '0.85rem',
                        textTransform: 'none',
                        py: 0.8,
                        border: `1px solid ${alpha('#fff', 0.08)}`,
                        borderRadius: 1.5,
                        '&:hover': {
                            color: cyan,
                            borderColor: alpha(cyan, 0.3),
                            backgroundColor: alpha(cyan, 0.04),
                        },
                    }}
                >
                    Or start a guided session  {'\u2192'}
                </Button>
            </Box>
        </Box>
    );
}

function InstagramLanding() {
    const [showStickyCta, setShowStickyCta] = useState(false);
    const [faqExpanded, setFaqExpanded] = useState(false);
    const postsRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            const heroHeight = window.innerHeight * 0.8;
            setShowStickyCta(window.scrollY > heroHeight);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToPosts = () => {
        if (postsRef.current) {
            postsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <>
            <Helmet>
                <title>ReaderFlex Instagram Guides | Read Faster, Focus Better, Remember More</title>
                <meta name="description" content="Find the ReaderFlex guide you saw on Instagram. Learn how to read faster, stop rereading, improve comprehension, remember more, and train your focus." />
                <link rel="canonical" href="https://readerflex.com/instagram" />
                <meta property="og:title" content="You clicked because reading should feel easier than this." />
                <meta property="og:description" content="Find the ReaderFlex Instagram guide that brought you here and start training your reading speed, focus, and comprehension." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://readerflex.com/instagram" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="ReaderFlex Instagram Guides | Read Faster, Focus Better, Remember More" />
                <meta name="twitter:description" content="Find the ReaderFlex guide you saw on Instagram. Learn how to read faster, stop rereading, improve comprehension, remember more, and train your focus." />
            </Helmet>

            <Box sx={{ backgroundColor: darkBg, color: '#fff' }}>
                {/* 1. HERO */}
                <Box
                    sx={{
                        position: 'relative',
                        minHeight: { xs: '85vh', md: '92vh' },
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'hidden',
                        background: `radial-gradient(ellipse at 30% 40%, ${alpha('#0A1A2E', 0.8)} 0%, ${darkBg} 70%)`,
                    }}
                >
                    {/* Glowing dot motif background */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            opacity: 0.4,
                            backgroundImage: `radial-gradient(circle at 20% 30%, ${alpha(cyan, 0.06)} 0%, transparent 40%), radial-gradient(circle at 80% 70%, ${alpha(cyan, 0.04)} 0%, transparent 40%)`,
                            zIndex: 0,
                        }}
                    />
                    {/* Animated guide dots trail */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: '15%',
                            left: 0,
                            right: 0,
                            height: 2,
                            zIndex: 0,
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: -4,
                                left: '10%',
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: cyan,
                                animation: `${guideDotMove} 3s ease-in-out infinite alternate, ${glowPulse} 2s ease-in-out infinite`,
                            },
                        }}
                    />

                    <GuideDotHero />

                    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                        <Box sx={{ maxWidth: { xs: '100%', md: '55%' } }}>
                            <Typography
                                variant="overline"
                                sx={{
                                    color: cyan,
                                    letterSpacing: '0.12em',
                                    fontSize: '0.75rem',
                                    display: 'block',
                                    mb: 2,
                                }}
                            >
                                ReaderFlex x Instagram
                            </Typography>
                            <Typography
                                variant="h2"
                                component="h1"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem' },
                                    lineHeight: 1.15,
                                    letterSpacing: '-0.02em',
                                    mb: 3,
                                    color: '#fff',
                                }}
                            >
                                You clicked because reading should feel easier than{' '}
                                <Box component="span" sx={{ color: cyan }}>this.</Box>
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: { xs: '1rem', sm: '1.2rem' },
                                    lineHeight: 1.6,
                                    color: alpha('#fff', 0.7),
                                    fontWeight: 400,
                                    mb: 4,
                                    maxWidth: '90%',
                                }}
                            >
                                Find the ReaderFlex guide from Instagram, learn what is slowing you down, and start training your reading speed, focus, and comprehension today.
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                                <Button
                                    component={RouterLink}
                                    to="/register"
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        backgroundColor: cyan,
                                        color: '#000',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        boxShadow: `0 0 24px ${alpha(cyan, 0.4)}`,
                                        '&:hover': {
                                            backgroundColor: alpha(cyan, 0.85),
                                            boxShadow: `0 0 32px ${alpha(cyan, 0.6)}`,
                                        },
                                    }}
                                >
                                    Start Reading Faster
                                </Button>
                                <Button
                                    onClick={scrollToPosts}
                                    variant="outlined"
                                    size="large"
                                    sx={{
                                        borderColor: alpha('#fff', 0.3),
                                        color: '#fff',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        '&:hover': {
                                            borderColor: cyan,
                                            color: cyan,
                                            backgroundColor: alpha(cyan, 0.05),
                                        },
                                    }}
                                >
                                    Find My Instagram Post
                                </Button>
                            </Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: alpha('#fff', 0.35),
                                    fontSize: '0.85rem',
                                    fontStyle: 'italic',
                                }}
                            >
                                Built for speed, comprehension, focus, and retention - not empty skimming.
                            </Typography>
                        </Box>
                    </Container>
                </Box>

                {/* 2. WHICH POST BROUGHT YOU HERE */}
                <Box ref={postsRef} sx={{ py: { xs: 6, md: 10 }, scrollMarginTop: 20 }}>
                    <Container maxWidth="lg">
                        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
                            <Typography
                                variant="h3"
                                component="h2"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '1.6rem', sm: '2.2rem', md: '2.5rem' },
                                    color: '#fff',
                                    letterSpacing: '-0.02em',
                                    mb: 2,
                                }}
                            >
                                Which ReaderFlex post brought you here?
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                    color: alpha('#fff', 0.55),
                                    fontWeight: 400,
                                    maxWidth: '600px',
                                    mx: 'auto',
                                }}
                            >
                                Tap the post you saw. Get the full guide. Then try the tool that helps you train it.
                            </Typography>
                        </Box>
                        <Grid container spacing={3}>
                            {instagramPosts.map((post, i) => (
                                <Grid item xs={12} sm={6} md={4} key={post.num}>
                                    <InstagramCard post={post} index={i} />
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Box>

                {/* 3. PRODUCT CONVERSION SECTION */}
                <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: alpha('#000', 0.3) }}>
                    <Container maxWidth="lg">
                        <Grid container spacing={6} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Typography
                                    variant="h3"
                                    component="h2"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.3rem' },
                                        color: '#fff',
                                        letterSpacing: '-0.02em',
                                        mb: 3,
                                        lineHeight: 1.25,
                                    }}
                                >
                                    ReaderFlex does not just teach reading tips. It helps you{' '}
                                    <Box component="span" sx={{ color: cyan }}>train them.</Box>
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: alpha('#fff', 0.65),
                                        fontSize: '1.05rem',
                                        lineHeight: 1.7,
                                        mb: 4,
                                    }}
                                >
                                    Most reading advice tells you what to do, then leaves you alone with a wall of text. ReaderFlex gives your eyes something to follow, your pace something to lock onto, and your brain a cleaner rhythm for moving through words.
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                                    <Button
                                        component={RouterLink}
                                        to="/register"
                                        variant="contained"
                                        size="large"
                                        sx={{
                                            backgroundColor: cyan,
                                            color: '#000',
                                            fontWeight: 700,
                                            textTransform: 'none',
                                            fontSize: '1rem',
                                            px: 4,
                                            py: 1.5,
                                            borderRadius: 2,
                                            boxShadow: `0 0 20px ${alpha(cyan, 0.3)}`,
                                            '&:hover': {
                                                backgroundColor: alpha(cyan, 0.85),
                                                boxShadow: `0 0 28px ${alpha(cyan, 0.5)}`,
                                            },
                                        }}
                                    >
                                        Try ReaderFlex Now
                                    </Button>
                                    <Button
                                        component={RouterLink}
                                        to="/blog"
                                        variant="text"
                                        size="large"
                                        sx={{
                                            color: alpha('#fff', 0.5),
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            fontSize: '1rem',
                                            px: 3,
                                            '&:hover': {
                                                color: cyan,
                                                backgroundColor: 'transparent',
                                            },
                                        }}
                                    >
                                        Read the Guides First
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box
                                    sx={{
                                        backgroundColor: cardBg,
                                        border: `1px solid ${cardBorder}`,
                                        borderRadius: 3,
                                        p: { xs: 3, md: 4 },
                                    }}
                                >
                                    {featureBullets.map((bullet, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 2,
                                                py: 1.5,
                                                borderBottom: i < featureBullets.length - 1 ? `1px solid ${alpha('#fff', 0.06)}` : 'none',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    background: cyan,
                                                    mt: 0.7,
                                                    flexShrink: 0,
                                                    boxShadow: `0 0 8px ${alpha(cyan, 0.6)}`,
                                                }}
                                            />
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: alpha('#fff', 0.8),
                                                    fontSize: '0.95rem',
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                {bullet}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>

                {/* 4. THE READERFLEX METHOD */}
                <Box sx={{ py: { xs: 6, md: 10 } }}>
                    <Container maxWidth="lg">
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.3rem' },
                                color: '#fff',
                                letterSpacing: '-0.02em',
                                mb: 2,
                                textAlign: 'center',
                            }}
                        >
                            The ReaderFlex Method
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                color: alpha('#fff', 0.5),
                                fontWeight: 400,
                                textAlign: 'center',
                                mb: { xs: 4, md: 6 },
                            }}
                        >
                            Three steps that change how you move through text.
                        </Typography>
                        <Grid container spacing={3}>
                            {methodCards.map((card, i) => (
                                <Grid item xs={12} md={4} key={i}>
                                    <Box
                                        sx={{
                                            backgroundColor: cardBg,
                                            border: `1px solid ${cardBorder}`,
                                            borderRadius: 3,
                                            p: 4,
                                            height: '100%',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                borderColor: alpha(cyan, 0.35),
                                                boxShadow: `0 8px 32px ${alpha(cyan, 0.1)}`,
                                            },
                                        }}
                                    >
                                        <Box sx={{ mb: 2 }}>{card.icon}</Box>
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                color: '#fff',
                                                fontWeight: 700,
                                                fontSize: '1.25rem',
                                                mb: 2,
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {card.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: alpha('#fff', 0.6),
                                                fontSize: '0.9rem',
                                                lineHeight: 1.65,
                                            }}
                                        >
                                            {card.copy}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Box>

                {/* 5. INSTAGRAM VISITOR OFFER + TRY THE GUIDE DOT */}
                <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: alpha('#000', 0.3) }}>
                    <Container maxWidth="md">
                        <Box
                            sx={{
                                textAlign: 'center',
                                backgroundColor: `linear-gradient(135deg, ${alpha(cyan, 0.08)}, ${alpha('#000', 0.4)})`,
                                background: `linear-gradient(135deg, ${alpha(cyan, 0.08)}, ${alpha('#000', 0.4)})`,
                                border: `1px solid ${alpha(cyan, 0.2)}`,
                                borderRadius: 4,
                                p: { xs: 4, md: 6 },
                                boxShadow: `0 0 60px ${alpha(cyan, 0.06)}`,
                            }}
                        >
                            <Typography
                                variant="overline"
                                sx={{
                                    color: yellow,
                                    letterSpacing: '0.1em',
                                    fontSize: '0.75rem',
                                    display: 'block',
                                    mb: 2,
                                }}
                            >
                                Instagram Visitor Offer
                            </Typography>
                            <Typography
                                variant="h4"
                                component="h2"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.1rem' },
                                    color: '#fff',
                                    letterSpacing: '-0.02em',
                                    mb: 3,
                                    lineHeight: 1.3,
                                }}
                            >
                                Came from Instagram? Start with one focused reading session.
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: alpha('#fff', 0.65),
                                    fontSize: '1.05rem',
                                    lineHeight: 1.7,
                                    mb: 4,
                                    maxWidth: '520px',
                                    mx: 'auto',
                                }}
                            >
                                Open ReaderFlex, choose a short article, turn on the Guide Dot, and see how different reading feels when your eyes have a rhythm.
                            </Typography>
                            <Button
                                component={RouterLink}
                                to="/register"
                                variant="contained"
                                size="large"
                                sx={{
                                    backgroundColor: cyan,
                                    color: '#000',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                    px: 5,
                                    py: 1.5,
                                    borderRadius: 2,
                                    boxShadow: `0 0 24px ${alpha(cyan, 0.4)}`,
                                    '&:hover': {
                                        backgroundColor: alpha(cyan, 0.85),
                                        boxShadow: `0 0 36px ${alpha(cyan, 0.6)}`,
                                    },
                                }}
                            >
                                Start My First Session
                            </Button>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: alpha('#fff', 0.3),
                                    fontSize: '0.85rem',
                                    fontStyle: 'italic',
                                    mt: 2,
                                }}
                            >
                                No pressure. Just one focused session.
                            </Typography>
                        </Box>
                    </Container>
                </Box>

                {/* 6. FAQ */}
                <Box sx={{ py: { xs: 6, md: 10 } }}>
                    <Container maxWidth="md">
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.3rem' },
                                color: '#fff',
                                letterSpacing: '-0.02em',
                                mb: { xs: 3, md: 5 },
                                textAlign: 'center',
                            }}
                        >
                            Questions You Might Have
                        </Typography>
                        {faqs.map((faq, i) => (
                            <Accordion
                                key={i}
                                expanded={faqExpanded === i}
                                onChange={() => setFaqExpanded(faqExpanded === i ? false : i)}
                                sx={{
                                    backgroundColor: cardBg,
                                    border: `1px solid ${cardBorder}`,
                                    borderRadius: '12px !important',
                                    mb: 2,
                                    '&:before': { display: 'none' },
                                    '&.Mui-expanded': {
                                        borderColor: alpha(cyan, 0.3),
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ color: cyan }} />}
                                    sx={{
                                        '&:hover': { backgroundColor: alpha(cyan, 0.03) },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: '#fff',
                                            fontWeight: 600,
                                            fontSize: '1rem',
                                        }}
                                    >
                                        {faq.q}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography
                                        sx={{
                                            color: alpha('#fff', 0.6),
                                            fontSize: '0.95rem',
                                            lineHeight: 1.7,
                                        }}
                                    >
                                        {faq.a}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Container>
                </Box>

                {/* 7. FINAL CTA */}
                <Box
                    sx={{
                        py: { xs: 8, md: 12 },
                        textAlign: 'center',
                        background: `radial-gradient(ellipse at center, ${alpha('#0A1A2E', 0.6)} 0%, ${darkBg} 70%)`,
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '20%',
                            left: '15%',
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: cyan,
                            opacity: 0.5,
                            animation: `${glowPulse} 2s ease-in-out infinite`,
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: '25%',
                            right: '20%',
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: cyan,
                            opacity: 0.3,
                            animation: `${glowPulse} 2.5s ease-in-out infinite 0.5s`,
                        }}
                    />
                    <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '1.6rem', sm: '2.2rem', md: '2.8rem' },
                                color: '#fff',
                                letterSpacing: '-0.02em',
                                mb: 3,
                                lineHeight: 1.2,
                            }}
                        >
                            Stop rereading. Start training your rhythm.
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                fontSize: { xs: '0.95rem', sm: '1.15rem' },
                                color: alpha('#fff', 0.55),
                                fontWeight: 400,
                                lineHeight: 1.6,
                                mb: 5,
                                maxWidth: '540px',
                                mx: 'auto',
                            }}
                        >
                            ReaderFlex gives your eyes a path, your pace a rhythm, and your brain a better way to move through text.
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/register"
                            variant="contained"
                            size="large"
                            sx={{
                                backgroundColor: cyan,
                                color: '#000',
                                fontWeight: 700,
                                textTransform: 'none',
                                fontSize: '1.15rem',
                                px: 5,
                                py: 1.5,
                                borderRadius: 2,
                                boxShadow: `0 0 30px ${alpha(cyan, 0.4)}`,
                                mb: 3,
                                '&:hover': {
                                    backgroundColor: alpha(cyan, 0.85),
                                    boxShadow: `0 0 40px ${alpha(cyan, 0.6)}`,
                                },
                            }}
                        >
                            Start Reading Faster with ReaderFlex
                        </Button>
                        <Box>
                            <Link
                                component={RouterLink}
                                to="/blog"
                                sx={{
                                    color: alpha('#fff', 0.4),
                                    fontSize: '0.9rem',
                                    textDecoration: 'none',
                                    '&:hover': { color: cyan },
                                }}
                            >
                                Browse All Instagram Guides
                            </Link>
                        </Box>
                    </Container>
                </Box>

                {/* STICKY MOBILE CTA */}
                <Fade in={showStickyCta}>
                    <Box
                        sx={{
                            display: { xs: 'flex', md: 'none' },
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: alpha('#060B14', 0.95),
                            borderTop: `1px solid ${alpha(cyan, 0.2)}`,
                            p: 2,
                            zIndex: 1000,
                            backdropFilter: 'blur(10px)',
                            justifyContent: 'center',
                        }}
                    >
                        <Button
                            component={RouterLink}
                            to="/register"
                            variant="contained"
                            fullWidth
                            sx={{
                                backgroundColor: cyan,
                                color: '#000',
                                fontWeight: 700,
                                textTransform: 'none',
                                fontSize: '1rem',
                                py: 1.2,
                                borderRadius: 2,
                                boxShadow: `0 0 16px ${alpha(cyan, 0.3)}`,
                                '&:hover': {
                                    backgroundColor: alpha(cyan, 0.85),
                                },
                            }}
                        >
                            Try ReaderFlex
                        </Button>
                    </Box>
                </Fade>
            </Box>
        </>
    );
}

export default InstagramLanding;
